import React from 'react';
import { Collapse } from 'antd';
import { dynamicSort } from '../../../logic/Utility/Utils';
import _ from 'lodash';
import styled, { useTheme } from 'styled-components';
import { IOrderLine } from '../../../logic/Models/Responses/OrderResponse';

import { themeGet } from '@styled-system/theme-get';
import { ReactComponent as RedAvailabilityIcon } from '../../../assets/images/AvailabilityIcons/Red.svg';
import { ReactComponent as PlusIcon } from '../../../assets/images/PlusIcon.svg';
import { SeriesAvailability } from '../SeriesAvailability';

/** List of seeds from the prior year. */
export const PriorYearSeedList = (props: {
	available: IOrderLine[];
	unavailable: IOrderLine[];
	isAlreadyInPlan: (oldSeed: IOrderLine) => boolean;
	onAddToPlan: (order: IOrderLine) => void;
}) => 
{
	const {
		available,
		unavailable,
	} = props;
	const theme = useTheme();

	return (
		<StyledCollapse
			style={{
				background: 'white',
				fontSize: theme.fontSizes.normal,
				fontWeight: theme.fontWeights.bold,
				height: '100%'
			}}
			bordered={false}
		>
			<StyledCollapsePanel header='Prior Year Order Data' key='PriorYearCollapse'>
				<div 
					className='PriorYearRowsContainer' 
					style={{ 
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						overflowY: 'auto',
						paddingRight: 10 
					}}
				>
					{available.sort(dynamicSort('RM')).map(seed =>
						<OrderHybridRow
							key={seed.Id}
							disabled={props.isAlreadyInPlan(seed)}
							orderSeed={seed}
							onAddToPlan={props.onAddToPlan}
						/>
					)}
					{unavailable.sort(dynamicSort('RM')).map(seed =>
						<OrderHybridRow
							key={seed.Id}
							disabled
							orderSeed={seed}
							onAddToPlan={() => {}}
						/>
					)}
				</div>
			</StyledCollapsePanel>
		</StyledCollapse>
	);
};

const StyledCollapse = styled(Collapse)`
	.ant-collapse-content-box {
		height: 100%;
	}
	.ant-collapse-content {
		height: 100%;
	}
`;
const StyledCollapsePanel = styled(Collapse.Panel)`
	height: 100%;
`;

/** Show one prior year seed's details. */
const OrderHybridRow = (props: {
	orderSeed: IOrderLine;
	disabled: boolean;
	onAddToPlan?: (order: IOrderLine) => void;
}) =>
{
	const {
		disabled,
		orderSeed,
		onAddToPlan,
	} = props;

	const theme = useTheme();

	return (
		<SeriesRow className='OrderRow' key={`OrderProduct${orderSeed.Id}`}>
			<SeriesAvailability className='SeriesAvailability' disabled={disabled}>
				{/* Currently order projections from the back-end do not include availability data. Restore this if that changes. ~mburroughs 7/29/21 */}
				{/* {orderSeed.availability == 'Green' ? <GreenAvailabilityIcon />
					: orderSeed.availability === 'Yellow' ? <YellowAvailabilityIcon />
						: <RedAvailabilityIcon />
				} */}
				<RedAvailabilityIcon />
			</SeriesAvailability>
			<div style={{ display: 'flex', flexDirection: 'row', flexGrow: 1, paddingLeft: 30 }}>
				<div style={{ display: 'flex', flexDirection: 'column', width: '30%' }}>
					<SeriesData className='HybridName' disabled={disabled}>
						{orderSeed.HybridName}
					</SeriesData>
					<SeriesData className='Treatment' style={{ fontSize: theme.fontSizes.tiny }} disabled={disabled}>
						{orderSeed.Treatment}
					</SeriesData>
				</div>				
				<SeriesData className='HybridRm' style={{ width: '20%' }} disabled={disabled}>
					RM: {orderSeed.RM}
				</SeriesData>
				<SeriesData 
					className='OrderUnits' 
					style={{ 
						width: '25%', 
						fontWeight: theme.fontWeights.normal 
					}} 
					disabled={disabled}
				>
					Units: {orderSeed.Quantity}
				</SeriesData>
				<SeriesData 
					className='OrderPackage' 
					style={{ 
						width: '25%', 
						fontWeight: theme.fontWeights.normal 
					}} 
					disabled={disabled}
				>
					Package Type: {orderSeed.Packaging}
				</SeriesData>
			</div>
			<SeriesAction className='SeriesAction' disabled={disabled}
				onClick={() => onAddToPlan ? onAddToPlan(orderSeed): undefined}
			>
				Add to Plan 
				<PlusIcon 
					style={{ marginLeft: 10 }} 
					fill={disabled ? theme.colors.disabledGrey : theme.colors.darkGrey} 
					width={12} 
					height={12} 
				/>
			</SeriesAction>
		</SeriesRow>
	);
};

const SeriesRow = styled.div`
	display: flex;
	flex-direction: row;
	width: 100%;
	height: 50px;
	min-height: 50px;
	background-color: ${themeGet('colors.white')};
	border: 1px solid ${themeGet('colors.lightestGrey')};
	border-radius: 5px;
	margin-bottom: 8px;
	align-items: center;
	font-family: ${themeGet('fonts.base')};
	font-size: ${themeGet('fontSizes.extraSmall')};
`;

const SeriesData = styled.div<{ disabled?: boolean }>`
	height: 100%;
	display: flex;
	align-items: center;
	text-align: center;
	color: ${props => props.disabled ? themeGet('colors.lightGrey') : themeGet('colors.darkGrey')};
`;

const SeriesAction = styled.div<{ disabled?: boolean }>`
	height: 100%;
	min-width: 150px;
    display: flex;
    align-items: center;
    padding: 17px 24px;
    cursor: pointer;
	font-weight: ${themeGet('fontSizes.bold')};
	border-left: thin solid ${themeGet('colors.lightestGrey')};
	color: ${props => props.disabled ? themeGet('colors.lightGrey') : 'black'};
	cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
`;
